import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { taskDocumentLoaded, TaskDocumentLoadedPayload } from "../../processOverview/store/actions";
import { assertNonNullable, assertTrue } from "../../../utilities/assert";
import { isArrayOf, isOptionalOf } from "../../../utilities/typeGuardUtils";
import { createCustomNotificationAction } from "../../../app/actions";
import { modulfInvalidateClientNotification } from "../../../utilities/rpcHelper";

import { isClientNotification } from "../types";

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * Invalidation on the server is an asynchronous round-trip, and the document can be re-loaded (or re-synced from
 * the process variables) before it completes. Remembering what was already shown keeps it at one toast per
 * notification.
 */
const deliveredNotificationUuids = new Set<string>();

export function* deliverProcessNotificationsSaga(): SagaIterator {
	yield* takeLatest(taskDocumentLoaded, deliverProcessNotificationsWorker);
}

function* deliverProcessNotificationsWorker({
	payload: { activityId, document }
}: Action<TaskDocumentLoadedPayload>): SagaGenerator<void> {
	const clientNotifications = documentService.getAssignedObject(
		document as GroupInstance,
		ModelPath.fromString("/root/processMetadata/clientNotifications").map((it, index) => ({
			...it,
			index: [1, 1, 0][index]
		}))
	);

	assertTrue(isOptionalOf(isArrayOf(isClientNotification))(clientNotifications));
	assertNonNullable(document?.id);

	const currentTaskKey = document.root.processMetadata.task.key;
	const notificationUuidsToRemove = [];
	for (const { title, message, level, uuid, addressee: originTaskKey } of clientNotifications ?? []) {
		if (deliveredNotificationUuids.has(uuid) || originTaskKey === currentTaskKey) {
			continue;
		}
		deliveredNotificationUuids.add(uuid);
		yield* put(
			createCustomNotificationAction({
				title: { key: title },
				message: { key: message },
				icon: level === "success" ? "done" : level,
				severity: level,
				activityId,
				group: "processLinking"
			})
		);

		notificationUuidsToRemove.push(uuid);
	}

	if (notificationUuidsToRemove.length > 0) {
		yield* call(modulfInvalidateClientNotification, document.id, notificationUuidsToRemove);
	}
}
